import { Turnstile } from "@marsidev/react-turnstile";
import React, { useEffect, useRef } from "react";
import { TurnstileEvents } from "../analytics";
import { dispatchTrack } from "../analytics/dispatch";

const nowMs = (): number =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : Date.now();

interface BotDetectionProps {
  siteKey: string;
  onVerify?: (botDetectionToken: string) => void;
  onError?: (error: string) => void;
  timeoutMs?: number;
  onTimeout?: (dummyToken: string) => void;
  page?: string;
}

const BotDetection: React.FC<BotDetectionProps> = ({
  siteKey,
  onVerify,
  onError,
  timeoutMs,
  onTimeout,
  page,
}) => {
  const hasVerifiedRef = useRef(false);
  const hasEmittedTerminalRef = useRef(false);
  const mountTimeRef = useRef<number>(nowMs());

  useEffect(() => {
    if (!timeoutMs || !onTimeout) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (!hasVerifiedRef.current) {
        // Send dummy token instead of empty/missing token to leverage backend's fail-open logic.
        // When Gandalf receives "TURNSTILE_UNAVAILABLE", it attempts verification with Cloudflare.
        // If Cloudflare is experiencing outages (network error), Gandalf fails open and allows login.
        // If Cloudflare is operational (returns invalid token), Gandalf rejects with 400 error.
        // This maintains security while providing resilience during actual Cloudflare service disruptions.
        onTimeout("TURNSTILE_UNAVAILABLE");
        if (!hasEmittedTerminalRef.current) {
          const { event, properties } = TurnstileEvents.Timeout(timeoutMs, page);
          dispatchTrack(event, properties);
          hasEmittedTerminalRef.current = true;
        }
      }
    }, timeoutMs);

    return () => clearTimeout(timeoutId);
  }, [timeoutMs, onTimeout, page]);

  const onVerifyHandler = (token: string) => {
    hasVerifiedRef.current = true;
    if (!hasEmittedTerminalRef.current) {
      const elapsed = nowMs() - mountTimeRef.current;
      const { event, properties } = TurnstileEvents.Verified(elapsed, page);
      dispatchTrack(event, properties);
      hasEmittedTerminalRef.current = true;
    }
    onVerify?.(token);
  };

  const onErrorHandler = (error: string) => {
    const { event, properties } = TurnstileEvents.Error(error, page);
    dispatchTrack(event, properties);

    const parsed = Number(error);
    // The error messages sent from the Turnstile script can be numeric codes (e.g., load or network errors).
    // If the error is a finite number, treat it as a generic captcha loading failure and show a retry message.
    if (Number.isFinite(parsed)) {
      onError?.(
        " Error loading captcha. Please try refreshing the page. If the error persists, please contact support"
      );
      return;
    }
    onError?.(" Unknown error has occurred. If the error persists, please contact support");
  };

  return <Turnstile siteKey={siteKey} onSuccess={onVerifyHandler} onError={onErrorHandler} />;
};

export default BotDetection;
